import {ChangeDetectorRef, Directive, ElementRef, EventEmitter, inject, OnDestroy, Output, ViewChild} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {LinkRecordsModal, LinkRecordsModalConfig} from './link-records.modal';

/**
 * UNIVAPLAT-3694: shared base of the linked-records tabs (organisation detail tabs and the
 * contract-side Organisations tab), cloned from ContractAdminsTabComponent, which stays untouched.
 *
 * Covers load with a stale-load guard, unlink with a double-submit guard, the link popup, toasts
 * and focus handling. Subclasses supply the table markup (a `#linkButton` template reference on
 * the link button restores focus after the popup closes) and the abstract members; they call
 * `load()` when their inputs change. `changed` fires after a successful link or unlink so the
 * parent page can refresh anything that depends on the links.
 *
 * Backend owns the user-facing error text. Row data (staff names, emails, reasons) is personal
 * data: never logged, and dropped when the tab is destroyed.
 */
@Directive()
export abstract class LinkedRecordsTabBase<T> implements OnDestroy {
    /** Emits after a successful link or unlink. */
    @Output() changed = new EventEmitter<void>();

    rows: T[] = [];
    loading = false;
    loadError: string | null = null;
    /** True while an unlink request runs (double-submit guard; also disables the unlink buttons). */
    removing = false;

    @ViewChild('linkButton') protected linkButton?: ElementRef<HTMLButtonElement>;

    protected readonly modalService = inject(NgbModal);
    protected readonly toastr = inject(ToastrService);
    protected readonly cdr = inject(ChangeDetectorRef);

    private loadSeq = 0;

    /** Loads the linked rows. */
    protected abstract fetch(): Promise<T[]>;

    /** Removes one link. */
    protected abstract unlinkOne(row: T): Promise<void>;

    /** Singular label used in the success toast, e.g. `Care programme` gives `Care programme unlinked`. */
    protected abstract readonly entityLabel: string;

    /** Message shown when the rows cannot be loaded and the backend gave no text. */
    protected get loadErrorMessage(): string {
        return `The ${this.entityLabel.toLowerCase()} list could not be loaded.`;
    }

    /** Whether link / unlink are offered (permission and active state). */
    abstract get canWrite(): boolean;

    abstract rowId(row: T): number;

    /** Popup configuration for the link action. `any`: the candidate type differs per tab and is only read by its own columns. */
    protected abstract linkModalConfig(): LinkRecordsModalConfig<any>;

    ngOnDestroy(): void {
        this.loadSeq++;
        this.rows = [];
    }

    trackById = (_index: number, row: T): number => this.rowId(row);

    async load(): Promise<void> {
        const seq = ++this.loadSeq;
        this.loadError = null;
        this.loading = true;
        try {
            const rows = await this.fetch();
            if (seq === this.loadSeq) {
                this.rows = rows;
            }
        } catch (error: any) {
            if (seq === this.loadSeq) {
                this.rows = [];
                this.loadError = error?.error?.error ?? this.loadErrorMessage;
            }
        } finally {
            if (seq === this.loadSeq) {
                this.loading = false;
            }
        }
    }

    async openLinkModal(): Promise<void> {
        if (!this.canWrite) {
            return;
        }
        const config = this.linkModalConfig();
        const ref = this.modalService.open(LinkRecordsModal, {
            size: 'lg',
            centered: true,
            ariaLabelledBy: config.titleId
        });
        Object.assign(ref.componentInstance, config);
        const linked = await ref.result.catch(() => false);
        if (linked) {
            await this.load();
            this.changed.emit();
        }
        // NgbModal returns focus to the opener on close; re-assert it after the refresh re-rendered it.
        this.focusLinkButton();
    }

    async unlink(row: T): Promise<void> {
        if (this.removing || !this.canWrite) {
            return;
        }
        this.removing = true;
        try {
            await this.unlinkOne(row);
            this.toastr.success(`${this.entityLabel} unlinked`, 'Success');
            await this.load();
            this.changed.emit();
            this.focusLinkButton();
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
        } finally {
            this.removing = false;
        }
    }

    protected focusLinkButton(): void {
        this.cdr.detectChanges();
        this.linkButton?.nativeElement.focus();
    }
}
